import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { submitServiceRequest } from '../api/userApi';
import { getUserGoals } from '../api/userGoalApi';
import '../Style/DonationFlow.css';

const RequestFormStep = ({ paymentData }) => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [requestType, setRequestType] = useState('GOAL_CHECK');
  const [goals, setGoals] = useState([]);
  const [selectedGoalId, setSelectedGoalId] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [selectedGoalIds, setSelectedGoalIds] = useState([]);

  // Date limits — today and 20 days back
  const today = new Date().toISOString().split('T')[0];
  const minDate = new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  useEffect(() => {
    getUserGoals()
      .then((data) => {
        console.log('GOALS:', data); // check what comes back
        const list = Array.isArray(data) ? data : data.content || [];
        setGoals(list);
      })
      .catch((err) => console.error('Failed to load goals', err));
  }, []);

  const handleSubmit = async () => {
    if (submitted) {
      setError('This payment has already been used. Please start a new request.');
      return;
    }
    if (requestType === 'FREE_TEXT' && !customMessage.trim()) {
      setError('Please describe your request.');
      return;
    }

    if (requestType === 'GOAL_CHECK') {
      if (selectedGoalIds.length === 0) {
        setError('Please select at least one goal.');
        return;
      }

      if (!targetDate) {
        setError('Please select a date to check.');
        return;
      }
    }

    setSubmitted(true);
    setLoading(true);
    setError(null);

    try {
      console.log('selectedGoalId:', selectedGoalId, 'parsed:', parseInt(selectedGoalId));
      await submitServiceRequest({
        donationAmount: paymentData.amount,
        stripePaymentId: paymentData.paymentIntentId,
        requestType,
        goalIds: requestType === 'GOAL_CHECK' ? selectedGoalIds : null,
        targetDate: requestType === 'GOAL_CHECK' ? targetDate : null,
        customMessage: requestType === 'FREE_TEXT' ? customMessage : null,
      });
      navigate('/service-request/success');
    } catch (err) {
      setError('Failed to submit request. Please contact support.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="request-form-step">
      <h2 className="step-title">What do you need?</h2>
      <p className="step-subtitle">Payment confirmed ✅ — now describe your request.</p>

      {/* Type toggle */}
      <div className="request-type-toggle">
        <button
          className={`type-btn ${requestType === 'GOAL_CHECK' ? 'active' : ''}`}
          onClick={() => setRequestType('GOAL_CHECK')}
        >
          🎯 Goal Check
        </button>
        <button
          className={`type-btn ${requestType === 'FREE_TEXT' ? 'active' : ''}`}
          onClick={() => setRequestType('FREE_TEXT')}
        >
          ✏️ Custom Request
        </button>
      </div>

      {/* GOAL_CHECK form */}
      {requestType === 'GOAL_CHECK' && (
        <div className="form-fields">
          <label className="form-label">Select Goals (one or more)</label>
          <div className="goal-checkbox-list">
            {goals.map((g) => (
              <label key={g.goalId} className="goal-checkbox-item">
                <input
                  type="checkbox"
                  value={g.goalId}
                  checked={selectedGoalIds.includes(g.goalId)}
                  onChange={(e) => {
                    const id = g.goalId;
                    setSelectedGoalIds((prev) => {
                      if (e.target.checked && prev.length >= 5) return prev; // block if at limit
                      return e.target.checked ? [...prev, id] : prev.filter((x) => x !== id);
                    });
                  }}
                />
                <span>{g.goalTitle}</span>
              </label>
            ))}
            <div className="goal-select-counter">
              {selectedGoalIds.length}/5 selected
              {selectedGoalIds.length >= 5 && (
                <span className="goal-limit-warning"> — Maximum reached</span>
              )}
            </div>
          </div>

          <label className="form-label">Date to Check</label>
          <input
            type="date"
            className="form-date"
            value={targetDate}
            min={minDate}
            max={today}
            onChange={(e) => setTargetDate(e.target.value)}
          />
        </div>
      )}

      {/* FREE_TEXT form */}
      {requestType === 'FREE_TEXT' && (
        <div className="form-fields">
          <label className="form-label">Describe your request</label>
          <textarea
            className="form-textarea"
            rows={4}
            maxLength={1000}
            placeholder="Explain what you need help with..."
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
          />
          <span className="char-count">{customMessage.length}/1000</span>
        </div>
      )}

      {error && <p className="donation-error">{error}</p>}

      <button className="donate-btn" onClick={handleSubmit} disabled={loading}>
        {loading ? 'Submitting...' : 'Submit Request'}
      </button>
    </div>
  );
};

export default RequestFormStep;
